import axios from 'axios';
import ApiService from './ApiService';

/**
 * Telna Connect API client.
 *
 * A SEPARATE file from ApiService.js on purpose. ApiService is a 3000-line
 * shared surface that every page in the panel imports; adding a dozen more
 * static methods to it makes an unrelated merge conflict likely and buries the
 * Telna calls among Books, Affiliates and Drive. Telna is its own provider with
 * its own routes, so it gets its own client.
 *
 * The base URL is reused from ApiService rather than re-declared — that file
 * carries the dev/prod switch, and a second copy of it would go stale the first
 * time someone flipped it.
 */

/** Every Telna admin route is behind the admin JWT; the token is the same one. */
const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: 'Bearer ' + localStorage.getItem('token'),
});

const base = () => `${ApiService.baseURL}/telna`;

/**
 * Unwrap an axios error into something a page can show.
 *
 * The backend answers a consistent envelope — `{ success:false, code, message,
 * requestId }` — and `code` is the part that matters: the panel has to tell
 * "Telna is unreachable" apart from "provisioning is switched off", and those
 * are a 502 and a 409 with different codes, not two flavours of "failed".
 */
const toError = (error, fallback) => {
    const d = error?.response?.data;
    const err = new Error(d?.message || d?.error || error.message || fallback);
    err.code = d?.code || 'REQUEST_FAILED';
    err.status = error?.response?.status;
    err.requestId = d?.requestId;
    return err;
};

class TelnaApiService {
    /* ------------------------------------------------------ provider state -- */

    /** Is the key configured, and can the backend actually reach Telna? */
    static async getStatus() {
        try {
            const res = await axios.get(`${base()}/status`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not read Telna status'); }
    }

    /** Prepaid wallet balance — packages draw against this. */
    static async getWallet() {
        try {
            const res = await axios.get(`${base()}/wallet`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not read the Telna wallet'); }
    }

    /* ----------------------------------------------------------- catalogue -- */

    /** Package templates: the plans Telna has put on our shelf. */
    static async getPackageTemplates() {
        try {
            const res = await axios.get(`${base()}/package-templates`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load the Telna catalogue'); }
    }

    static async getPackageTemplate(templateId) {
        try {
            const res = await axios.get(`${base()}/package-templates/${templateId}`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load that package template'); }
    }

    /** ISO-2/ISO-3 country list. Templates list coverage in ISO-3. */
    static async getCountries() {
        try {
            const res = await axios.get(`${base()}/countries`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load the country list'); }
    }

    /* ----------------------------------------------------------- inventory -- */

    /** Telna's SIM stock, cross-referenced with our own reservations. */
    static async getSims(params = {}) {
        try {
            const res = await axios.get(`${base()}/sims`, { headers: authHeaders(), params });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load Telna SIM stock'); }
    }

    /**
     * Take an eSIM out of our ledger.
     *
     * `force` is NOT sent by default. The server refuses a delivered eSIM
     * without it — its activation code is out and cannot be revoked — and
     * that refusal is the guard, not a formality. The error it returns
     * explains itself and is surfaced verbatim.
     */
    static async removeEsim(iccid, force = false) {
        try {
            const res = await axios.delete(`${base()}/esims/${iccid}`, {
                headers: authHeaders(),
                params: force ? { force: 'true' } : undefined,
            });
            return res.data;
        } catch (error) { throw toError(error, 'Could not remove that eSIM'); }
    }

    /** One ICCID in full: activation code, profile state, packages, sessions. */
    static async getSim(iccid) {
        try {
            const res = await axios.get(`${base()}/sims/${iccid}`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load that eSIM'); }
    }

    /** Live packages across the inventory, or for one ICCID. */
    static async getPackages(params = {}) {
        try {
            const res = await axios.get(`${base()}/packages`, { headers: authHeaders(), params });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load Telna packages'); }
    }

    /* -------------------------------------------------------------- orders -- */

    /** Dashboard tiles: order counts, sellable stock, wallet, the spend switch. */
    static async getOrderStats() {
        try {
            const res = await axios.get(`${base()}/orders/stats`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load Telna statistics'); }
    }

    static async getOrders(params = {}) {
        try {
            const res = await axios.get(`${base()}/orders`, { headers: authHeaders(), params });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load Telna orders'); }
    }

    /** One order, with a rendered QR per delivered line and live package state. */
    static async getOrder(orderNumber) {
        try {
            const res = await axios.get(`${base()}/orders/${orderNumber}`, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load that order'); }
    }

    /**
     * Create an order.
     *
     * Writes nothing upstream and spends nothing unless `provision: true` is
     * passed AND the backend's spend switch is on.
     */
    static async createOrder(payload) {
        try {
            const res = await axios.post(`${base()}/orders`, payload, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not create the order'); }
    }

    /** Buy the eSIMs for an order. SPENDS WALLET BALANCE when enabled. */
    static async provisionOrder(orderNumber) {
        try {
            const res = await axios.post(`${base()}/orders/${orderNumber}/provision`, {}, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Provisioning failed'); }
    }

    /** Clear one failed line so the next provisioning run picks it up. */
    static async retryOrderItem(orderNumber, index) {
        try {
            const res = await axios.post(`${base()}/orders/${orderNumber}/items/${index}/retry`, {}, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not reset that line'); }
    }

    /** Email the QR codes to the customer. */
    static async sendOrderEmail(orderNumber) {
        try {
            const res = await axios.post(`${base()}/orders/${orderNumber}/send-email`, {}, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not send the email'); }
    }

    /** Cancel. Refused once any line is delivered — Telna does not refund. */
    static async cancelOrder(orderNumber) {
        try {
            const res = await axios.patch(`${base()}/orders/${orderNumber}/cancel`, {}, { headers: authHeaders() });
            return res.data;
        } catch (error) { throw toError(error, 'Could not cancel the order'); }
    }

    /** The local ledger of issued ICCIDs, as opposed to Telna's inventory. */
    static async getIssuedEsims(params = {}) {
        try {
            const res = await axios.get(`${base()}/esims`, { headers: authHeaders(), params });
            return res.data;
        } catch (error) { throw toError(error, 'Could not load issued eSIMs'); }
    }
}

export default TelnaApiService;
